/* ============================================================
   Everlé — theme JS
   Scroll reveals (GSAP), product gallery, variant price sync,
   live cart count.
   ============================================================ */

// Guard: if GSAP failed to load, reveal everything so the page never looks broken
const revealFallback = () => {
  document
    .querySelectorAll("[data-reveal]")
    .forEach((el) => el.classList.add("is-visible"));
};

// Scroll reveal animations
const initReveals = () => {
  // Theme editor: no scroll animations — a re-rendered section would
  // otherwise stay at opacity 0 and the preview looks blank/white
  if (window.Shopify?.designMode) {
    revealFallback();
    return;
  }

  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    revealFallback();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (prefersReducedMotion) {
    revealFallback();
    return;
  }

  document.querySelectorAll("[data-reveal]").forEach((el) => {
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      ease: "power2.out",
      scrollTrigger: {
        trigger: el,
        start: "top 88%",
        once: true,
      },
    });
  });
};

// Hero parallax: the overscaled image drifts as you scroll past the hero
const initHeroParallax = () => {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined")
    return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const img = document.querySelector("[data-hero-parallax] .hero__img");
  if (!img) return;

  gsap.registerPlugin(ScrollTrigger);
  gsap.to(img, {
    yPercent: 10,
    ease: "none",
    scrollTrigger: {
      trigger: "[data-hero-parallax]",
      start: "top top",
      end: "bottom top",
      scrub: true,
    },
  });
};

// Product gallery: swipeable slider (mobile) synced with thumbnails (desktop)
const initGallery = () => {
  const slider = document.querySelector("[data-gallery-slider]");
  if (!slider) return;

  const slides = slider.querySelectorAll(".product__slide");
  const thumbs = document.querySelectorAll(".product__thumb");
  const dots = document.querySelectorAll(".product__dot");
  if (slides.length < 2) return;

  const setActive = (index) => {
    thumbs.forEach((t, i) => t.classList.toggle("is-active", i === index));
    dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
  };

  const goTo = (index) => {
    const clamped = Math.min(Math.max(index, 0), slides.length - 1);
    slider.scrollTo({ left: slides[clamped].offsetLeft, behavior: "smooth" });
  };

  const currentIndex = () => Math.round(slider.scrollLeft / slider.clientWidth);

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener("click", () => goTo(index));
  });

  document
    .querySelector("[data-gallery-prev]")
    ?.addEventListener("click", () => goTo(currentIndex() - 1));
  document
    .querySelector("[data-gallery-next]")
    ?.addEventListener("click", () => goTo(currentIndex() + 1));

  // Track swipe position without flooding the main thread
  let ticking = false;
  slider.addEventListener(
    "scroll",
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const index = Math.round(slider.scrollLeft / slider.clientWidth);
        setActive(Math.min(Math.max(index, 0), slides.length - 1));
      });
    },
    { passive: true },
  );
};

// Variant selection: keep displayed price in sync with the chosen variant
const initVariantPrice = () => {
  const form = document.getElementById("product-form");
  if (!form) return;

  const priceEl = document.querySelector("[data-price]");
  const compareEl = document.querySelector("[data-compare-price]");
  const stickyPriceEl = document.querySelector("[data-sticky-price]");

  form.querySelectorAll('input[name="id"][type="radio"]').forEach((input) => {
    input.addEventListener("change", () => {
      // If a bundle tier is selected, its pack total owns the displayed price
      const bundleChecked = form.querySelector(
        'input[name="quantity"][type="radio"]:checked',
      );
      if (bundleChecked) {
        bundleChecked.dispatchEvent(new Event("change"));
        return;
      }
      if (priceEl && input.dataset.price)
        priceEl.textContent = input.dataset.price;
      if (stickyPriceEl && input.dataset.price)
        stickyPriceEl.textContent = input.dataset.price;
      if (compareEl) {
        compareEl.textContent = input.dataset.comparePrice || "";
        compareEl.style.display = input.dataset.comparePrice ? "" : "none";
      }
    });
  });
};

// Bundle selector: picking a pack updates the displayed prices (main + sticky bar).
// The radio itself posts as the native "quantity" field, so adding to cart needs no JS.
const initBundleOffer = () => {
  const form = document.getElementById("product-form");
  if (!form) return;

  const radios = form.querySelectorAll('input[name="quantity"][type="radio"]');
  if (!radios.length) return;

  const priceEl = document.querySelector("[data-price]");
  const compareEl = document.querySelector("[data-compare-price]");
  const stickyPriceEl = document.querySelector("[data-sticky-price]");

  radios.forEach((input) => {
    input.addEventListener("change", () => {
      form
        .querySelectorAll(".bundle-tier")
        .forEach((tier) =>
          tier.classList.toggle("is-selected", tier.contains(input)),
        );
      if (priceEl) priceEl.textContent = input.dataset.total;
      if (stickyPriceEl) stickyPriceEl.textContent = input.dataset.total;
      if (compareEl) {
        compareEl.textContent = input.dataset.compare || "";
        compareEl.style.display = input.dataset.compare ? "" : "none";
      }
    });
  });
};

// Sticky buy bar (mobile): show once the main buy button scrolls above the viewport
const initStickyBuy = () => {
  const bar = document.getElementById("product-sticky-bar");
  const buyBtn = document.querySelector(".product__buy");
  if (!bar || !buyBtn || !("IntersectionObserver" in window)) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle("is-visible", show);
      bar.setAttribute("aria-hidden", String(!show));
      // Lets CSS lift the floating discount pill clear of the bar
      document.body.classList.toggle("sticky-buy-visible", show);
    },
    { threshold: 0 },
  );
  observer.observe(buyBtn);
};

// Cart page: changing a quantity re-submits the cart form automatically,
// so prices update without pressing Enter or "Update cart"
const initCartAutoUpdate = () => {
  const form = document.querySelector(".cart__form");
  if (!form) return;

  form.querySelectorAll('input[name="updates[]"]').forEach((input) => {
    input.addEventListener("change", () => {
      // NOTE: never disable the inputs here — disabled fields are excluded
      // from the form POST and the new quantity would be lost
      form.querySelectorAll("button").forEach((el) => (el.disabled = true));
      form.submit();
    });
  });
};

// Cart count: refresh after add-to-cart submissions (AJAX-free fallback keeps working)
const refreshCartCount = async () => {
  const countEl = document.querySelector("[data-cart-count]");
  if (!countEl) return;

  try {
    const response = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;
    const cart = await response.json();
    countEl.textContent = cart.item_count;
  } catch (error) {
    // Non-critical: keep the server-rendered count
    console.warn("Cart count refresh failed", error);
  }
};

// Floating discount pill: delayed reveal, click-to-copy, dismiss persists for the session
const DISCOUNT_DISMISS_KEY = "everle-discount-dismissed";
const DISCOUNT_SHOW_DELAY_MS = 4000;

const initDiscountFloat = () => {
  const pill = document.getElementById("discount-float");
  if (!pill) return;

  // sessionStorage can throw in private/incognito modes — never let that break the page
  let dismissed = false;
  try {
    dismissed = sessionStorage.getItem(DISCOUNT_DISMISS_KEY) === "1";
  } catch (error) {
    dismissed = false;
  }
  if (dismissed) return;

  // Delayed reveal so it never competes with the hero on first paint
  setTimeout(() => {
    pill.hidden = false;
    requestAnimationFrame(() => pill.classList.add("is-visible"));
  }, DISCOUNT_SHOW_DELAY_MS);

  document
    .getElementById("discount-float-close")
    ?.addEventListener("click", () => {
      pill.classList.remove("is-visible");
      setTimeout(() => (pill.hidden = true), 300);
      try {
        sessionStorage.setItem(DISCOUNT_DISMISS_KEY, "1");
      } catch (error) {
        // Non-critical: the pill just reappears on the next page
      }
    });

  const copyBtn = document.getElementById("discount-float-copy");
  copyBtn?.addEventListener("click", async () => {
    const codeEl = pill.querySelector(".discount-float__code");
    if (!codeEl) return;
    try {
      await navigator.clipboard.writeText(pill.dataset.code);
      const original = codeEl.textContent;
      codeEl.textContent = pill.dataset.copiedLabel;
      setTimeout(() => (codeEl.textContent = original), 1600);
    } catch (error) {
      // Clipboard unavailable (http/permissions): the code is still visible to type manually
      console.warn("Clipboard copy failed", error);
    }
  });
};

// Theme editor: when a section re-renders after a setting change, its
// [data-reveal] elements come back hidden — show them and re-wire the UI
document.addEventListener("shopify:section:load", (event) => {
  event.target
    .querySelectorAll("[data-reveal]")
    .forEach((el) => el.classList.add("is-visible"));
  initGallery();
  initBundleOffer();
  initStickyBuy();
  initDiscountFloat();
});

document.addEventListener("DOMContentLoaded", () => {
  initReveals();
  initHeroParallax();
  initGallery();
  initVariantPrice();
  initBundleOffer();
  initStickyBuy();
  initCartAutoUpdate();
  refreshCartCount();
  initDiscountFloat();
});
